import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Gauge,
  Lightbulb,
  Minimize2,
  RefreshCcw,
  ShieldCheck,
  Target,
  TrendingUp,
} from "lucide-react";
import { EASE, Eyebrow, Reveal, fadeUp, staggerContainer } from "@/components/site/motion";
import OrbitVisual from "@/components/site/OrbitVisual";
import { contact } from "@/data/site";

const values = [
  { title: "Innovation", desc: "We continuously explore better technologies, architectures, and ways of solving problems.", icon: Lightbulb },
  { title: "Engineering Excellence", desc: "We value performance, maintainability, reliability, and thoughtful technical decisions.", icon: Gauge },
  { title: "Simplicity", desc: "Complex technology should become easier to use, operate, and understand.", icon: Minimize2 },
  { title: "Reliability", desc: "We aim to build systems and relationships that remain dependable over time.", icon: ShieldCheck },
  { title: "Scalability", desc: "Technology should be ready to evolve as products and businesses grow.", icon: TrendingUp },
  { title: "Continuous Improvement", desc: "Good products and systems improve through iteration, learning, and refinement.", icon: RefreshCcw },
  { title: "Client Impact", desc: "Technology should create meaningful operational or business value.", icon: Target },
];

const philosophy = [
  { title: "Understand Before Building", desc: "Start with the problem, users, workflows, and desired outcomes." },
  { title: "Choose Technology Deliberately", desc: "Select tools and architecture around the product rather than trends." },
  { title: "Design for Change", desc: "Build foundations that can adapt as requirements evolve." },
  { title: "Think Beyond Launch", desc: "Consider maintenance, observability, support, security, and future growth from the beginning." },
];

const companyInfo = [
  ["Legal Name", "Bytevora IT Solutions Private Limited"],
  ["Brand", "Bytevora"],
  ["Company Type", "Private company limited by shares"],
  ["Incorporation Date", "19 August 2026"],
  ["CIN", contact.cin],
  ["GSTIN", contact.gst],
  ["Registered State", "Uttar Pradesh, India"],
  ["Registered Office", contact.registeredOffice],
];

const futureAreas = ["Services", "Consulting", "SaaS", "Platforms", "APIs", "Automation", "Proprietary Software", "Emerging Technology"];

export default function AboutPage() {
  return (
    <main className="bg-white" data-testid="about-page">
      {/* Hero */}
      <section className="relative overflow-hidden pt-[72px]">
        <div className="bg-grid-light absolute inset-0" aria-hidden="true" />
        <div className="absolute -right-40 top-[-120px] h-[420px] w-[420px] rounded-full bg-violet-600/10 blur-[120px]" aria-hidden="true" />
        <div className="relative mx-auto grid w-full max-w-[1200px] items-center gap-14 px-6 py-20 md:px-8 md:py-28 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}>
              <Eyebrow>About Bytevora</Eyebrow>
            </motion.div>
            <motion.h1
              data-testid="about-hero-heading"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.95, ease: EASE, delay: 0.12 }}
              className="mt-6 text-[clamp(2.6rem,5.5vw,4.6rem)] font-extrabold leading-[1.02] tracking-[-0.03em] text-slate-900"
            >
              Engineering technology{" "}
              <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
                with purpose.
              </span>
            </motion.h1>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: EASE, delay: 0.28 }}
            >
              <p className="mt-6 max-w-[560px] text-base leading-relaxed text-slate-600 md:text-lg">
                Bytevora is a technology company focused on designing, engineering, and scaling
                modern software, digital products, cloud platforms, and intelligent systems.
              </p>
              <p className="mt-4 max-w-[560px] text-sm leading-relaxed text-slate-500 md:text-base">
                We combine product thinking, practical engineering, and modern technology to help
                businesses turn ideas and operational challenges into solutions built for long-term use.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link
                  to="/services"
                  data-testid="about-explore-services-button"
                  className="group inline-flex items-center gap-2.5 rounded-[10px] bg-navy px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-blue-600"
                >
                  Explore Our Services
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/contact"
                  data-testid="about-start-project-link"
                  className="group inline-flex items-center gap-2 px-2 py-3.5 text-sm font-semibold text-slate-900 transition-colors duration-300 hover:text-blue-600"
                >
                  Start a Project
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/team"
                  data-testid="about-team-link"
                  className="group inline-flex items-center gap-2 px-2 py-3.5 text-sm font-semibold text-slate-900 transition-colors duration-300 hover:text-blue-600"
                >
                  Our Team
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: EASE, delay: 0.35 }}
            className="hidden lg:col-span-5 lg:block"
          >
            <OrbitVisual />
          </motion.div>
        </div>
      </section>

      {/* Who We Are */}
      <section className="py-24 md:py-28" data-testid="about-who-we-are">
        <div className="mx-auto grid w-full max-w-[1200px] gap-10 px-6 md:px-8 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Who we are</Eyebrow>
            <h2 className="mt-5 text-3xl font-bold leading-[1.1] tracking-[-0.02em] text-slate-900 md:text-[2.5rem]">
              A technology company built for modern digital challenges.
            </h2>
          </Reveal>
          <Reveal delay={1} className="flex flex-col justify-end gap-5">
            <p className="text-[15px] leading-[1.8] text-slate-600 md:text-base">
              Bytevora IT Solutions Private Limited is an Indian technology company focused on
              software engineering, digital products, cloud, AI, automation, infrastructure, and
              technology consulting.
            </p>
            <p className="text-[15px] leading-[1.8] text-slate-600 md:text-base">
              Our role is not simply to deliver code. We aim to understand the business problem,
              shape the right technical approach, and build systems that remain useful,
              maintainable, and scalable as requirements evolve.
            </p>
          </Reveal>
        </div>
      </section>

      {/* What We're Building */}
      <section className="bg-[#F8FAFC] py-24 md:py-28" data-testid="about-what-were-building">
        <div className="mx-auto w-full max-w-[1200px] px-6 md:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow>Our direction</Eyebrow>
            <h2 className="mt-5 text-3xl font-bold leading-[1.1] tracking-[-0.02em] text-slate-900 md:text-[2.5rem]">
              More than a services company.
            </h2>
            <p className="mt-5 text-[15px] leading-[1.8] text-slate-600 md:text-base">
              Bytevora is being built around both technology services and long-term product
              capability. Alongside client-focused software engineering, cloud, AI, automation,
              and consulting, the company&rsquo;s broader scope supports the development and
              commercialisation of proprietary software, SaaS platforms, APIs, algorithms, and
              other technology products.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-5 md:grid-cols-2">
            <Reveal className="h-full">
              <div className="h-full rounded-[20px] border border-slate-200 bg-white p-8 md:p-10">
                <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-blue-600">Services</p>
                <h3 className="mt-4 text-xl font-semibold tracking-tight text-slate-900">
                  Helping businesses build, modernize, automate, and scale through technology.
                </h3>
                <div className="mt-7 flex flex-wrap gap-2">
                  {["Software Engineering", "Web & Mobile", "Cloud", "AI & Automation", "Digital Transformation", "Technology Consulting"].map((c) => (
                    <span key={c} className="rounded-full border border-slate-200 bg-slate-50 px-3.5 py-1.5 text-[12.5px] font-medium text-slate-600">
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={1} className="h-full">
              <div className="h-full rounded-[20px] border border-slate-200 bg-navy p-8 text-white md:p-10">
                <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-violet-400">Products</p>
                <h3 className="mt-4 text-xl font-semibold tracking-tight text-white">
                  Building the foundation for future Bytevora-owned digital products and platforms.
                </h3>
                <div className="mt-7 flex flex-wrap gap-2">
                  {["SaaS", "APIs", "Automation Platforms", "Cloud Applications", "Proprietary Software", "Intelligent Systems"].map((c) => (
                    <span key={c} className="rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-[12.5px] font-medium text-slate-300">
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 md:py-28" data-testid="about-mission-vision">
        <div className="mx-auto w-full max-w-[1200px] px-6 md:px-8">
          <Reveal>
            <Eyebrow>Mission &amp; vision</Eyebrow>
          </Reveal>
          <div className="mt-10 grid gap-12 md:grid-cols-2 md:gap-16">
            {[
              { num: "01", label: "Mission", text: "To design and engineer modern technology solutions that help businesses innovate, operate efficiently, and build for the future." },
              { num: "02", label: "Vision", text: "To become a trusted technology company creating scalable digital products, intelligent platforms, and innovative solutions for businesses worldwide." },
            ].map((m, i) => (
              <Reveal key={m.label} delay={i} className="relative border-t-2 border-slate-900 pt-8">
                <span className="absolute -top-1 right-0 font-mono text-[64px] font-bold leading-none text-slate-100" aria-hidden="true">
                  {m.num}
                </span>
                <p className="font-mono text-[11px] uppercase tracking-[0.26em] text-slate-400">{m.label}</p>
                <p className="mt-5 max-w-md text-xl font-semibold leading-[1.5] tracking-[-0.01em] text-slate-900 md:text-[1.45rem]">
                  {m.text}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#F8FAFC] py-24 md:py-28" data-testid="about-values">
        <div className="mx-auto w-full max-w-[1200px] px-6 md:px-8">
          <Reveal className="max-w-xl">
            <Eyebrow>What guides us</Eyebrow>
            <h2 className="mt-5 text-3xl font-bold leading-[1.1] tracking-[-0.02em] text-slate-900 md:text-[2.5rem]">
              Principles behind how we build.
            </h2>
          </Reveal>
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            className="mt-14 grid gap-px overflow-hidden rounded-[20px] border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-3"
          >
            {values.map((v, i) => (
              <motion.div
                key={v.title}
                variants={fadeUp}
                data-testid={`value-${i}`}
                className={`group bg-white p-8 transition-colors duration-300 hover:bg-slate-50 ${i === values.length - 1 ? "sm:col-span-2 lg:col-span-3 lg:flex lg:items-center lg:gap-10" : ""}`}
              >
                <div className="flex items-center gap-4">
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-slate-200 bg-slate-50 text-slate-500 transition-colors duration-300 group-hover:border-blue-600/25 group-hover:text-blue-600">
                    <v.icon size={19} strokeWidth={1.7} />
                  </span>
                  <span className="font-mono text-xs tracking-[0.16em] text-slate-300">0{i + 1}</span>
                </div>
                <div>
                  <h3 className="mt-5 text-[17px] font-semibold tracking-tight text-slate-900 lg:mt-5">{v.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600 lg:max-w-md">{v.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Philosophy — dark */}
      <section className="relative overflow-hidden bg-navy py-24 text-white md:py-32" data-testid="about-philosophy">
        <div className="bg-grid-dark absolute inset-0" aria-hidden="true" />
        <div className="absolute -left-40 bottom-[-160px] h-[420px] w-[420px] rounded-full bg-blue-600/20 blur-[130px]" aria-hidden="true" />
        <div className="relative mx-auto w-full max-w-[1200px] px-6 md:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow dark>How we think about technology</Eyebrow>
            <h2 className="mt-5 text-3xl font-bold leading-[1.1] tracking-[-0.02em] text-white md:text-[2.5rem]">
              Technology is a tool. The problem comes first.
            </h2>
            <p className="mt-5 max-w-xl text-[15px] leading-[1.8] text-slate-400">
              We don&rsquo;t believe every project should use the same stack, architecture, or
              process. The right solution depends on the users, business goals, technical
              constraints, scale, security requirements, and long-term direction of the product.
            </p>
          </Reveal>
          <div className="mt-14 grid gap-px overflow-hidden rounded-[20px] border border-white/10 bg-white/10 sm:grid-cols-2">
            {philosophy.map((p, i) => (
              <Reveal key={p.title} delay={i} className="bg-navy p-8 transition-colors duration-300 hover:bg-navy-800">
                <span className="font-mono text-xs tracking-[0.16em] text-slate-500">0{i + 1}</span>
                <h3 className="mt-4 text-[17px] font-semibold tracking-tight text-white">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{p.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Company Information */}
      <section className="py-24 md:py-28" data-testid="about-company-info">
        <div className="mx-auto grid w-full max-w-[1200px] gap-12 px-6 md:px-8 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <Eyebrow>Company information</Eyebrow>
            <h2 className="mt-5 text-3xl font-bold leading-[1.1] tracking-[-0.02em] text-slate-900 md:text-[2.2rem]">
              Bytevora IT Solutions Private Limited
            </h2>
            <p className="mt-5 max-w-md text-[15px] leading-[1.8] text-slate-600">
              Bytevora operates as a private company limited by shares, incorporated in
              Uttar Pradesh, India.
            </p>
          </Reveal>
          <Reveal delay={1} className="lg:col-span-7">
            <div className="overflow-hidden rounded-[20px] border border-slate-200">
              {companyInfo.map(([k, v], i) => (
                <div
                  key={k}
                  className={`grid gap-1 px-7 py-5 sm:grid-cols-[200px_1fr] sm:gap-6 ${i % 2 === 0 ? "bg-white" : "bg-slate-50/60"} ${i !== 0 ? "border-t border-slate-100" : ""}`}
                >
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400 sm:pt-0.5">{k}</span>
                  <span className="text-sm font-medium text-slate-900">{v}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Company Story */}
      <section className="bg-[#F8FAFC] py-24 md:py-28" data-testid="about-story">
        <div className="mx-auto grid w-full max-w-[1200px] gap-10 px-6 md:px-8 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Our story</Eyebrow>
            <h2 className="mt-5 text-3xl font-bold leading-[1.1] tracking-[-0.02em] text-slate-900 md:text-[2.5rem]">
              Built for what comes next.
            </h2>
          </Reveal>
          <Reveal delay={1} className="flex flex-col justify-end gap-5">
            <p className="text-[15px] leading-[1.8] text-slate-600 md:text-base">
              Bytevora was established in 2026 with a broad technology mandate spanning software,
              digital products, cloud, AI, automation, infrastructure, and emerging technologies.
            </p>
            <p className="text-[15px] leading-[1.8] text-slate-600 md:text-base">
              Rather than limiting the company to one technology or service category, Bytevora is
              being built as a flexible technology organization capable of supporting client
              projects today while creating its own digital products and intellectual property
              over time.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Future Direction */}
      <section className="py-24 md:py-28" data-testid="about-future">
        <div className="mx-auto w-full max-w-[1200px] px-6 text-center md:px-8">
          <Reveal className="flex justify-center">
            <Eyebrow>Long-term opportunity</Eyebrow>
          </Reveal>
          <Reveal delay={1}>
            <h2 className="mx-auto mt-5 max-w-2xl text-3xl font-bold leading-[1.1] tracking-[-0.02em] text-slate-900 md:text-[2.5rem]">
              A platform for long-term technology innovation.
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-[15px] leading-[1.8] text-slate-600">
              Bytevora&rsquo;s long-term direction is broader than project delivery. The company is
              structured to support services, consulting, SaaS products, proprietary software,
              cloud applications, APIs, automation systems, and other technology intellectual property.
            </p>
          </Reveal>
          <Reveal delay={2} className="mt-10 flex flex-wrap justify-center gap-2.5">
            {futureAreas.map((a) => (
              <span
                key={a}
                className="rounded-full border border-slate-200 bg-white px-4 py-2 text-[13px] font-medium text-slate-700 transition-colors duration-300 hover:border-blue-600/40 hover:text-blue-600"
              >
                {a}
              </span>
            ))}
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-navy py-24 md:py-32" data-testid="about-cta">
        <div className="bg-grid-dark absolute inset-0" aria-hidden="true" />
        <div className="absolute -right-40 top-[-120px] h-[420px] w-[420px] rounded-full bg-violet-600/25 blur-[130px]" aria-hidden="true" />
        <div className="relative mx-auto max-w-[860px] px-6 text-center md:px-8">
          <Reveal>
            <h2 className="text-4xl font-extrabold leading-[1.05] tracking-[-0.03em] text-white sm:text-5xl">
              Let&rsquo;s build something{" "}
              <span className="bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent">
                that lasts.
              </span>
            </h2>
          </Reveal>
          <Reveal delay={1}>
            <p className="mx-auto mt-6 max-w-xl text-sm leading-relaxed text-slate-400 md:text-base">
              Whether you&rsquo;re building a new product, modernizing an existing system, or
              exploring AI, automation, or cloud, Bytevora can help shape the technical path forward.
            </p>
          </Reveal>
          <Reveal delay={2} className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              data-testid="about-cta-start-project"
              className="group inline-flex items-center gap-2.5 rounded-[10px] bg-white px-7 py-3.5 text-sm font-semibold text-navy transition-colors duration-300 hover:bg-blue-600 hover:text-white"
            >
              Start a Project
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/services"
              data-testid="about-cta-explore-services"
              className="group inline-flex items-center gap-2 px-2 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:text-blue-300"
            >
              Explore Services
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
